import { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ErrorAlert } from '@resideo/blueprint-react';
import { Checkbox, Toggle, useToastContext } from '@resideo/blueprint-oneportal';
import type { UpdateUserInviteDataModel } from '@resideo/web-integration-services-api-client';
import apiService, {
  DeviceFilter,
  type AccountsResponse,
  type ConsumerLocationDetail,
  type ConsumerUserDetailResponse,
} from '@/services/apiService';
import styles from './ManageDeviceAccessPage.module.css';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ManageDeviceAccessHandle {
  save: () => void;
}

export interface ManageDeviceAccessPageProps {
  consumerAccountId: string;
  consumerUserId: string;
  locationId: string;
  email: string;
  role: string;
  invitedUserId: string | null;
  readOnly?: boolean;
  onSavingChange?: (saving: boolean) => void;
  onChangesCountChange?: (count: number) => void;
  onClose: () => void;
}

interface CameraRow {
  id: string;
  name: string;
}

// Derived from the SDK model — avoids 'any' in the PUT body
type InviteRole = NonNullable<UpdateUserInviteDataModel['inviteDataInfo']>['role'];
type ScheduleInfo = NonNullable<NonNullable<UpdateUserInviteDataModel['inviteDataInfo']>['scheduleInfo']>;
type WeekDays = ScheduleInfo['weekDays'];

const ALL_WEEK_DAYS = [
  'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday',
] as unknown as WeekDays;

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatAddress(location: ConsumerLocationDetail): string {
  const a = location.address;
  if (!a) return '';
  const parts = [
    a.addressLine1,
    a.city,
    [a.stateProvinceRegionCode, a.zipPostalCode].filter(Boolean).join(' '),
  ].filter(Boolean);
  return parts.join(', ');
}

function extractAssignedCameraIds(
  detail: ConsumerUserDetailResponse,
  locId: string,
): string[] {
  return (
    detail.data.consumerAccount.locations
      .find(l => l.id === locId)
      ?.consumerDevices
      ?.filter(d => d.device?.product?.productFamily === 'Camera')
      ?.map(d => d.device?.deviceId ?? d.id)
      .filter((id): id is string => Boolean(id))
    ?? []
  );
}

function cameraRowsFromAccountsData(
  accountsData: AccountsResponse,
  locId: string,
): CameraRow[] {
  for (const cu of accountsData.data.consumerUsers ?? []) {
    const loc = cu.consumerAccount?.locations?.find(l => l.id === locId);
    if (loc) {
      return (loc.consumerDevices ?? [])
        .filter(d => d.device?.product?.productFamily === 'Camera')
        .map(d => ({
          id: d.device?.deviceId ?? d.id ?? '',
          name: d.name ?? 'Camera',
        }));
    }
  }
  return [];
}

// ─── Icon ─────────────────────────────────────────────────────────────────────

function CameraIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="25" height="22" viewBox="0 0 25 22" fill="none" aria-hidden="true" focusable="false">
      <path d="M18.2083 9.34548C17.656 9.34548 17.2083 9.7932 17.2083 10.3455C17.2083 10.8978 17.656 11.3455 18.2083 11.3455V10.3455V9.34548ZM22.4457 10.3455L22.4462 9.34548H22.4457V10.3455ZM23.4887 12.0337L24.3831 12.4808L24.3832 12.4806L23.4887 12.0337ZM21.1157 16.7808L22.01 17.2282L22.0101 17.2279L21.1157 16.7808ZM19.123 16.9372L19.9366 16.3558L19.9364 16.3554L19.123 16.9372ZM17.4584 12.8904C17.1371 12.4412 16.5125 12.3375 16.0633 12.6588C15.6141 12.98 15.5103 13.6046 15.8316 14.0539L16.645 13.4722L17.4584 12.8904ZM18.6237 6.90732L19.0713 6.0131L19.0713 6.01308L18.6237 6.90732ZM19.1452 8.47182L20.0396 8.91909L20.0398 8.9186L19.1452 8.47182ZM15.5215 15.718L14.6271 15.2707L14.6271 15.2707L15.5215 15.718ZM13.9558 16.2395L14.4034 15.3452L14.4034 15.3452L13.9558 16.2395ZM2.87833 10.6955L2.42847 11.5886L2.43078 11.5897L2.87833 10.6955ZM1.36167 6.13382L2.25673 6.57976L2.25801 6.57717L1.36167 6.13382ZM2.97167 2.87882L2.07786 2.43032L2.07532 2.43546L2.97167 2.87882ZM7.545 1.36215L7.09632 2.25584L7.09741 2.25639L7.545 1.36215ZM1 17.5122H0V19.5122H1V18.5122V17.5122ZM5.38667 18.5122L5.39363 17.5122H5.38667V18.5122ZM7.48667 17.2288L8.37972 17.6788L8.38233 17.6736L7.48667 17.2288ZM10.0623 14.2902L10.5071 13.3946L8.71575 12.5051L8.27101 13.4007L9.16667 13.8455L10.0623 14.2902ZM0 20.8455V21.8455H2V20.8455H1H0ZM2 16.1788V15.1788H0V16.1788H1H2ZM6.83333 5.84548C6.28105 5.84548 5.83333 6.2932 5.83333 6.84548C5.83333 7.39777 6.28105 7.84548 6.83333 7.84548V6.84548V5.84548ZM6.845 7.84548C7.39728 7.84548 7.845 7.39777 7.845 6.84548C7.845 6.2932 7.39728 5.84548 6.845 5.84548V6.84548V7.84548ZM18.2083 10.3455V11.3455H22.4457V10.3455V9.34548H18.2083V10.3455ZM22.4457 10.3455L22.4451 11.3455C22.4735 11.3455 22.5015 11.3528 22.5263 11.3666L23.0135 10.4934L23.5008 9.6201C23.1784 9.44021 22.8154 9.34568 22.4462 9.34548L22.4457 10.3455ZM23.0135 10.4934L22.5263 11.3666C22.5511 11.3804 22.5719 11.4004 22.5868 11.4246L23.4376 10.8989L24.2883 10.3733C24.0942 10.0593 23.8232 9.79998 23.5008 9.6201L23.0135 10.4934ZM23.4376 10.8989L22.5868 11.4246C22.6018 11.4487 22.6103 11.4763 22.6115 11.5047L23.6105 11.4597L24.6095 11.4147C24.5929 11.0459 24.4823 10.6874 24.2883 10.3733L23.4376 10.8989ZM23.6105 11.4597L22.6115 11.5047C22.6128 11.533 22.6068 11.5612 22.5941 11.5866L23.4887 12.0337L24.3832 12.4806C24.5482 12.1504 24.6261 11.7835 24.6095 11.4147L23.6105 11.4597ZM23.4887 12.0337L22.5942 11.5865L20.2212 16.3337L21.1157 16.7808L22.0101 17.2279L24.3831 12.4808L23.4887 12.0337ZM21.1157 16.7808L20.2213 16.3335C20.2085 16.3592 20.1892 16.3811 20.1653 16.3971L20.7239 17.2266L21.2824 18.0561C21.5923 17.8474 21.8429 17.5623 22.01 17.2282L21.1157 16.7808ZM20.7239 17.2266L20.1653 16.3971C20.1415 16.4132 20.1139 16.4228 20.0853 16.4251L20.1635 17.422L20.2417 18.4189C20.6141 18.3897 20.9726 18.2647 21.2824 18.0561L20.7239 17.2266ZM20.1635 17.422L20.0853 16.4251C20.0566 16.4273 20.0279 16.4221 20.0019 16.41L19.5795 17.3164L19.1571 18.2228C19.4957 18.3806 19.8693 18.4481 20.2417 18.4189L20.1635 17.422ZM19.5795 17.3164L20.0019 16.41C19.9758 16.3978 19.9533 16.3792 19.9366 16.3558L19.123 16.9372L18.3094 17.5185C18.5265 17.8224 18.8186 18.0651 19.1571 18.2228L19.5795 17.3164ZM19.123 16.9372L19.9364 16.3554L17.4584 12.8904L16.645 13.4722L15.8316 14.0539L18.3096 17.5189L19.123 16.9372ZM18.6237 6.90732L18.176 7.80153C18.2155 7.8213 18.2456 7.85595 18.2595 7.89786L19.2082 7.58163L20.1569 7.26541C19.9753 6.7206 19.5848 6.27017 19.0713 6.0131L18.6237 6.90732ZM19.2082 7.58163L18.2595 7.89786C18.2735 7.93977 18.2703 7.98551 18.2505 8.02503L19.1452 8.47182L20.0398 8.9186C20.2964 8.40483 20.3385 7.81021 20.1569 7.26541L19.2082 7.58163ZM19.1452 8.47182L18.2508 8.02455L14.6271 15.2707L15.5215 15.718L16.4159 16.1653L20.0396 8.91909L19.1452 8.47182ZM15.5215 15.718L14.6271 15.2707C14.6173 15.2903 14.6037 15.3078 14.5872 15.3221L15.2423 16.0777L15.8974 16.8332C16.1124 16.6468 16.2886 16.4198 16.4159 16.1652L15.5215 15.718ZM15.2423 16.0777L14.5872 15.3221C14.5707 15.3365 14.5515 15.3474 14.5307 15.3543L14.8467 16.3031L15.1627 17.2518C15.4327 17.1619 15.6824 17.0196 15.8974 16.8332L15.2423 16.0777ZM14.8467 16.3031L14.5307 15.3543C14.5099 15.3612 14.488 15.364 14.4661 15.3624L14.3949 16.3599L14.3237 17.3574C14.6076 17.3776 14.8927 17.3418 15.1627 17.2518L14.8467 16.3031ZM14.3949 16.3599L14.4661 15.3624C14.4443 15.3609 14.423 15.355 14.4034 15.3452L13.9558 16.2395L13.5082 17.1337C13.7628 17.2611 14.0399 17.3371 14.3237 17.3574L14.3949 16.3599ZM13.9558 16.2395L14.4034 15.3452L3.32589 9.80123L2.87833 10.6955L2.43078 11.5897L13.5083 17.1337L13.9558 16.2395ZM2.87833 10.6955L3.32819 9.80238C2.75977 9.51607 2.3279 9.01624 2.1271 8.41229L1.17817 8.72779L0.229247 9.04329C0.596923 10.1491 1.38768 11.0643 2.42848 11.5886L2.87833 10.6955ZM1.17817 8.72779L2.1271 8.41229C1.9263 7.80834 1.97291 7.14943 2.25673 6.57976L1.36167 6.13382L0.466604 5.68788C-0.0530845 6.73096 -0.138428 7.93743 0.229247 9.04329L1.17817 8.72779ZM1.36167 6.13382L2.25801 6.57717L3.86801 3.32217L2.97167 2.87882L2.07532 2.43546L0.46532 5.69046L1.36167 6.13382ZM2.97167 2.87882L3.86546 3.3273C4.00722 3.04479 4.20325 2.79296 4.44234 2.58621L3.78826 1.82979L3.13418 1.07337C2.69638 1.45193 2.33745 1.91303 2.07788 2.43033L2.97167 2.87882ZM3.78826 1.82979L4.44234 2.58621C4.68144 2.37946 4.95893 2.22184 5.25895 2.12234L4.94417 1.17317L4.6294 0.224007C4.08005 0.406189 3.57197 0.694804 3.13418 1.07337L3.78826 1.82979ZM4.94417 1.17317L5.25895 2.12234C5.55897 2.02284 5.87566 1.98342 6.19092 2.00633L6.26338 1.00896L6.33585 0.0115868C5.75861 -0.030356 5.17875 0.0418244 4.6294 0.224007L4.94417 1.17317ZM6.26338 1.00896L6.19092 2.00633C6.50618 2.02924 6.81383 2.11402 7.09632 2.25584L7.545 1.36215L7.99368 0.468456C7.47643 0.208775 6.9131 0.0535299 6.33585 0.0115868L6.26338 1.00896ZM7.545 1.36215L7.09741 2.25639L18.1761 7.80156L18.6237 6.90732L19.0713 6.01308L7.99259 0.467911L7.545 1.36215ZM1 18.5122V19.5122H5.38667V18.5122V17.5122H1V18.5122ZM5.38667 18.5122L5.37971 19.5121C6.00092 19.5165 6.61099 19.3471 7.14107 19.0231L6.61962 18.1699L6.09817 17.3166C5.88614 17.4462 5.64211 17.5139 5.39363 17.5122L5.38667 18.5122ZM6.61962 18.1699L7.14107 19.0231C7.67116 18.6992 8.10017 18.2336 8.37971 17.6788L7.48667 17.2288L6.59363 16.7788C6.48181 17.0008 6.31021 17.187 6.09817 17.3166L6.61962 18.1699ZM7.48667 17.2288L8.38233 17.6736L10.0623 14.2902L9.16667 13.8455L8.27101 13.4007L6.59101 16.7841L7.48667 17.2288ZM1 20.8455H2V16.1788H1H0V20.8455H1ZM6.83333 6.84548V7.84548H6.845V6.84548V5.84548H6.83333V6.84548Z" style={{ fill: '#605B51' }} />
    </svg>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export const ManageDeviceAccessPage = forwardRef<ManageDeviceAccessHandle, ManageDeviceAccessPageProps>(
  function ManageDeviceAccessPage({
    consumerAccountId,
    consumerUserId,
    locationId,
    email,
    role,
    invitedUserId,
    readOnly = false,
    onSavingChange,
    onChangesCountChange,
    onClose,
  }, ref) {
  const queryClient = useQueryClient();
  const { addToast } = useToastContext();
  const { t } = useTranslation();

  // ── Initial selection: all cameras assigned to this consumer across all locations ─
  const hasInitializedRef = useRef(false);

  const [localSelected, setLocalSelected] = useState<Set<string>>(() => {
    const cached = queryClient.getQueryData<ConsumerUserDetailResponse>([
      'consumerUserDetail', consumerAccountId, consumerUserId,
    ]);
    if (cached) {
      hasInitializedRef.current = true;
      const initial = new Set(extractAssignedCameraIds(cached, locationId));
      // Can't set initialSelectedRef here (render phase) — use a lazy ref trick
      return initial;
    }
    return new Set<string>();
  });
  const initialSelectedRef = useRef<Set<string> | null>(null);

  // ── API 1: assigned devices — reuses React Query cache populated by the
  //           Locations tab (same queryKey), so no extra network request ──────
  const { data: detailData } = useQuery({
    queryKey: ['consumerUserDetail', consumerAccountId, consumerUserId],
    queryFn: () => apiService.getConsumerUserDetail(consumerAccountId, consumerUserId),
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });

  // ── API 2: full account device inventory ──────────────────────────────────
  const { data: accountsData, isFetching: isLoadingAll } = useQuery({
    queryKey: ['consumerAccountsAll'],
    queryFn: () => apiService.getConsumerAccounts(undefined, DeviceFilter.ALL),
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });

  // ── Track initial selection to compute unsaved change count ─────────────

  // When data was already in cache, localSelected is set during render (not via
  // useEffect), so we capture the initial snapshot on the first paint instead.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (initialSelectedRef.current === null) {
      initialSelectedRef.current = new Set(localSelected);
    }
  }, []); // intentionally runs once on mount

  // Sync initial selection from API 1 when not already set from cache
  useEffect(() => {
    if (detailData && !hasInitializedRef.current) {
      const initial = new Set(extractAssignedCameraIds(detailData, locationId));
      setLocalSelected(initial);
      initialSelectedRef.current = initial;
      hasInitializedRef.current = true;
    }
  }, [detailData, locationId]);

  // Report changes count whenever localSelected changes
  useEffect(() => {
    if (!onChangesCountChange || !initialSelectedRef.current) return;
    const initial = initialSelectedRef.current;
    let count = 0;
    for (const id of localSelected) { if (!initial.has(id)) count++; }
    for (const id of initial) { if (!localSelected.has(id)) count++; }
    onChangesCountChange(count);
  }, [localSelected, onChangesCountChange]);

  // ── Derive camera list for this location ──────────────────────────────────

  // Cameras already assigned to the user at this location (API 1)
  const assignedCameras: CameraRow[] = (
    detailData?.data.consumerAccount.locations
      .find(l => l.id === locationId)
      ?.consumerDevices
      ?.filter(d => d.device?.product?.productFamily === 'Camera')
      .map(d => ({ id: d.device?.deviceId ?? d.id, name: d.name ?? 'Camera' }))
    ?? []
  );

  // All cameras at this location from the account (API 2 — superset, may include unassigned)
  const allCameras: CameraRow[] = accountsData
    ? cameraRowsFromAccountsData(accountsData, locationId)
    : [];

  // Once API 2 has loaded use its list. Fall back to API 1 while still fetching.
  const displayCameras: CameraRow[] = accountsData !== undefined ? allCameras : assignedCameras;

  const selectedCount = displayCameras.filter(c => localSelected.has(c.id)).length;

  // Location detail for header: try API 1 first, then API 2
  const currentLocationDetail: ConsumerLocationDetail | undefined =
    detailData?.data.consumerAccount.locations.find(l => l.id === locationId)
    ?? (() => {
      if (!accountsData) return undefined;
      for (const cu of accountsData.data.consumerUsers ?? []) {
        const loc = cu.consumerAccount?.locations?.find(l => l.id === locationId);
        if (loc) return loc as ConsumerLocationDetail;
      }
      return undefined;
    })();

  // ── Select-all derived state
  const allCamerasSelected =
    displayCameras.length > 0 && selectedCount === displayCameras.length;
  const someCamerasSelected = selectedCount > 0 && !allCamerasSelected;

  const selectAllRef = useRef<HTMLInputElement>(null);

  function handleSelectAll() {
    if (allCamerasSelected || someCamerasSelected) {
      setLocalSelected(new Set());
    } else {
      setLocalSelected(new Set(displayCameras.map(c => c.id)));
    }
  }

  // ── Cameras toggle — true when at least one camera is (or should be) selected
  const [camerasEnabled, setCamerasEnabled] = useState<boolean>(
    () => localSelected.size > 0,
  );

  // Sync enabled state once cameras load (in case localSelected starts empty
  // but selection is initialised asynchronously from the API)
  useEffect(() => {
    if (hasInitializedRef.current) {
      setCamerasEnabled(localSelected.size > 0);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasInitializedRef.current]);

  function toggleCamerasEnabled() {
    const turningOff = camerasEnabled;
    setCamerasEnabled(!turningOff);
    if (turningOff) {
      setLocalSelected(new Set());
    } else {
      setLocalSelected(new Set(displayCameras.map(c => c.id)));
    }
  }

  // ── Save state ─────────────────────────────────────────────────────────────
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // ── Escape key closes the overlay ─────────────────────────────────────────
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && !isSaving) onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isSaving, onClose]);

  // ── Handlers ──────────────────────────────────────────────────────────────

  function toggleCamera(id: string) {
    setLocalSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function handleSave() {
    setIsSaving(true);
    setSaveError(null);
    try {
      // Other locations retain their current assignments (from API 1)
      const allAssignedLocations: ConsumerLocationDetail[] =
        detailData?.data.consumerAccount.locations ?? [];

      const otherDeviceIds = allAssignedLocations
        .filter(l => l.id !== locationId)
        .flatMap(l => (l.consumerDevices ?? []).map(d => d.device?.deviceId ?? d.id))
        .filter((id): id is string => Boolean(id));

      // This location: whatever the user selected
      const newDeviceIds = [...otherDeviceIds, ...Array.from(localSelected)];

      const now = new Date();
      const expiresAt = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000).toISOString();

      const body: UpdateUserInviteDataModel = {
        invitedUserId: invitedUserId ?? undefined,
        inviteDataInfo: {
          role: role as InviteRole,
          sendToEmail: email,
          deviceIds: newDeviceIds,
          ...(role === 'GUEST' && {
            scheduleInfo: {
              weekDays: ALL_WEEK_DAYS,
              startDate: now.toISOString(),
              endDate: new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000).toISOString(),
              startTime: '00:01',
              endTime: '23:59',
            },
          }),
        },
        emailTemplateInfo: {
          templateLanguage: 'en-US',
          baseUrl: `${import.meta.env.VITE_RESIDEO_ID_URL}/mobile`,
          expiresAt,
        },
      };

      await apiService.updateInvite(consumerAccountId, body);
      await queryClient.invalidateQueries({
        queryKey: ['consumerUserDetail', consumerAccountId, consumerUserId],
      });
      addToast({ message: t('manageDeviceAccess.updateSaved'), toastType: 'Success', zIndex: '9999' });
      onClose();
    } catch {
      setSaveError(t('common.failedToSave'));
    } finally {
      setIsSaving(false);
    }
  }
  // ── Expose save to parent header via ref ──────────────────────────────────
  useImperativeHandle(ref, () => ({ save: handleSave }));

  useEffect(() => {
    onSavingChange?.(isSaving);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSaving]);
  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className={styles.body}>
      <div className={styles.content}>

        {saveError && (
          <ErrorAlert msg={saveError} />
        )}

        {/* ── One card for the selected location ──────────────────── */}
        <div className={styles.card}>

            {/* Location name + address */}
            {currentLocationDetail && (
              <div className={styles.locationRow}>
                <div className={styles.locationName}>{currentLocationDetail.name}</div>
                {formatAddress(currentLocationDetail) && (
                  <div className={styles.locationAddress}>
                    {formatAddress(currentLocationDetail)}
                  </div>
                )}
              </div>
            )}

            {/* Camera devices box */}
            <div className={styles.deviceRows}>
              <div className={styles.devicesBox}>

                {/* Category header: icon + label + count | Edit Access + Toggle */}
                <div className={styles.categoryRow}>
                  <div className={styles.categoryLeft}>
                    <span className={styles.categoryIcon}><CameraIcon /></span>
                    <span className={styles.categoryName}>Cameras</span>
                    <span className={styles.categoryCount}>
                      {selectedCount} of {displayCameras.length} cameras
                      {isLoadingAll && assignedCameras.length > 0 && (
                        <span className={styles.categoryLoadingHint}> · loading…</span>
                      )}
                    </span>
                  </div>
                  <div className={styles.categoryRight}>
                    <span className={styles.toggleWrapper}>
                      <Toggle
                        name="cameras-toggle"
                        checked={camerasEnabled}
                        onChange={readOnly ? undefined : toggleCamerasEnabled}
                        disabled={readOnly}
                      />
                    </span>
                  </div>
                </div>

                {/* Device rows — only shown when toggle is on */}
                {camerasEnabled && (
                  <div className={styles.devicesInnerBox}>
                    {/* Column header — select all row */}
                    <div className={styles.deviceHeaderRow}>
                      <Checkbox
                        ref={selectAllRef}
                        checked={allCamerasSelected}
                        indeterminate={someCamerasSelected}
                        onChange={readOnly ? undefined : handleSelectAll}
                        disabled={readOnly}
                      />
                      <span className={styles.deviceHeaderLabel}>Device</span>
                    </div>

                    {/* Device rows */}
                    {displayCameras.length === 0 ? (
                      <div className={styles.emptyRow}>No cameras found at this location.</div>
                    ) : (
                      displayCameras.map(camera => (
                        <div key={camera.id} className={styles.deviceRow}>
                          <Checkbox
                            checked={localSelected.has(camera.id)}
                            onChange={readOnly ? undefined : () => toggleCamera(camera.id)}
                            disabled={readOnly}
                          />
                          <span className={styles.deviceName}>{camera.name}</span>
                        </div>
                      ))
                    )}
                  </div>
                )}

              </div>{/* end .devicesBox */}
            </div>{/* end .deviceRows */}

          </div>{/* end .card */}

      </div>{/* end .content */}
    </div>
  );
  }
);
